<template>
  <div class="landing-layout">
    <LandingStickyHeader
      v-if="isDesktopViewport"
      :handle-c-t-a-button-click="handleCTAButtonClick"
      :show-sticky-header="showStickyHeader"
    />
    <LandingStickyMobileCta
      v-if="isMobileViewport"
      :cta-btn-title="$t('b2b__landing_page__generic__cta_btn__lets_go')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
      :show-sticky-cta="showStickyMobileCta"
    />
    <StartJourneyMsk
      :cta-btn-title="$t('b2b__landing_page__generic__cta_btn__lets_go')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
      :incentive="incentive"
      :receive-c-t-a-button-client-rect="receiveCTAButtonClientRect"
      :variant="variant"
    />
    <ReclaimingLife
      :cta-btn-title="$t('b2b__landing_page__generic__cta_btn__title')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
      :variant="variant"
    />
    <PainLocation :handle-c-t-a-button-click="handleCTAButtonClick" />
    <VideoTestimonials />
    <LandingSocialProof />
    <Landing3Steps
      :cta-btn-title="$t('b2b__landing_page__landing_3_steps__cta')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
    />
    <LandingConvenience
      :cta-btn-title="$t('b2b__landing_page__convenience__cta')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
    />
    <FaqModule />
    <Contacts />
    <LandingFinalCta
      :cta-btn-title="$t('b2b__landing_page__landing_3_steps__cta')"
      :handle-c-t-a-button-click="handleCTAButtonClick"
    />
    <FooterLanguage />
    <FooterCertificates />
    <FooterLinks />
  </div>
</template>

<script setup>
import { generateSignupContext, setPageTitle } from '@shared/utils'
import { computed, ref, watch } from 'vue'
import ROUTE from '@us/app/router/appModule/names'
import Contacts from '@us/app/components/Contacts.vue'
import FaqModule from '@shared/components/FaqModule.vue'
import Landing3Steps from '@shared/components/Landing3Steps.vue'
import LandingStickyHeader from '@shared/components/LandingStickyHeader.vue'
import LandingStickyMobileCta from '@us/app/components/LandingStickyMobileCta.vue'
import LandingSocialProof from '@shared/components/LandingSocialProof.vue'
import LandingConvenience from '@shared/components/LandingConvenience.vue'
import LandingFinalCta from '@shared/components/LandingFinalCta.vue'
import PainLocation from '@shared/components/PainLocation.vue'
import ReclaimingLife from '@shared/components/ReclaimingLife.vue'
import StartJourneyMsk from '@us/app/components/StartJourneyMSK.vue'
import { useI18n } from 'petite-vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import FooterLinks from '@us/app/components/FooterLinks.vue'
import FooterCertificates from '@us/app/components/FooterCertificates.vue'
import FooterLanguage from '@us/app/components/FooterLanguage.vue'
import useMetaPixel from '@shared/composables/useMetaPixel.js'
import VideoTestimonials from '@us/app/components/VideoTestimonials.vue'
import useUsStore from '@us/app/stores/useUsStore.js'
import useSharedStore from '@shared/stores/useSharedStore.js'
import useLandingPageStore from '@shared/stores/useLandingPageStore.js'
import useResponsiveness from '@shared/composables/useResponsiveness.js'

const { t } = useI18n()
const sharedStore = useSharedStore()
const usStore = useUsStore()
const landingPageStore = useLandingPageStore()
const responsiveness = useResponsiveness()

const router = useRouter()
const currentRoute = useRoute()

const landingPage = computed(() => landingPageStore.landingPage)
const getCTAButtonClientRect = ref(() => {})
const variant = computed(
  () => landingPage.value?.variant || sharedStore.initialQuery.variant,
)

const incentive = computed(() => landingPage.value?.incentive || null)
const displayTitle = computed(() => landingPage.value?.displayTitle || '')
const isDesktopViewport = computed(
  () => responsiveness.screenWidth.value >= 768,
)
const isMobileViewport = computed(() => !isDesktopViewport.value)
const MOBILE_STICKY_CTA_TRIGGER_OFFSET = 16

const showStickyHeader = () => {
  const rect = getCTAButtonClientRect.value()
  return rect != null && rect.top <= -rect.height / 2
}

const showStickyMobileCta = () => {
  const rect = getCTAButtonClientRect.value()
  return (
    rect != null &&
    rect.bottom <= rect.height + MOBILE_STICKY_CTA_TRIGGER_OFFSET
  )
}

const receiveCTAButtonClientRect = (clientRect) => {
  getCTAButtonClientRect.value = clientRect
}

const handleCTAButtonClick = () => {
  usStore.updateSignupContext(generateSignupContext())

  const resolvedRoute = router.resolve({
    name: ROUTE.SELECT_VERIFICATION_METHOD,
    params: { slug: landingPage.value?.slug },
    query: currentRoute.query,
  })
  window.open(resolvedRoute.href, '_blank', 'noopener')
}

watch(
  displayTitle,
  (title) => {
    setPageTitle(
      [
        title ? `${title} | ` : '',
        t('b2b__landing_page__generic__page_title'),
      ].join(''),
    )
  },
  { immediate: true },
)

const metaPixelKey = computed(() => landingPage.value?.metaPixelKey)
if (metaPixelKey.value && sharedStore.initialQuery?.utm_medium)
  useMetaPixel(metaPixelKey.value)
</script>

<style lang="scss">
.landing-layout {
  width: 100%;
  min-width: 320px;
  margin: 0 auto;
  overflow-x: clip;
  // Keep footer links accessible when the fixed mobile CTA is visible.
  padding-bottom: calc(84px + env(safe-area-inset-bottom, 0px));

  @include media-up(md) {
    padding-bottom: 0;
  }

  @media (max-width: 767px) {
    .start-journey__container__content {
      align-items: center;
      text-align: center;
    }

    .reclaiming-life__card,
    .landing-convenience__content,
    .three-steps__container,
    .video-testimonials__container,
    .landing-social-proof__container,
    .contacts,
    .landing-final-cta__container {
      text-align: center;
    }

    .pain-location__button {
      text-align: center;
    }
  }
}
</style>
